import type { NavigationItem } from './app-navigation';
import { AppPermissions } from './routes/permissions';

export interface NavigationAccess {
  isGuest: boolean;
  /** True when the user holds this permission (Admin is handled here, not by the caller). */
  has: (permission: AppPermissions) => boolean;
}

/**
 * The menu as one user sees it. Pure (no Angular) so `scripts/check-nav-permissions.js` can run the same rules.
 * Pages: their own gate plus every `alsoRequiredPermissions` gate must pass. Groups have no gate: a group stays when
 * one of its entries stays, or when its own landing page (`path`) passes the page gates. Headings stay when an entry under
 * them (up to the next heading) stays.
 */
export function filterNavigation(items: NavigationItem[], access: NavigationAccess): NavigationItem[] {
  const kept: NavigationItem[] = [];
  for (const item of items) {
    if (item.hidden) continue;
    if (item.heading) {
      kept.push(item);
      continue;
    }
    if (item.items) {
      const children = filterNavigation(item.items, access);
      if (children.some((c) => !c.heading) || (item.path && pageGatesPass(item, access))) kept.push({ ...item, items: children });
      continue;
    }
    if (pageGatesPass(item, access)) kept.push(item);
  }
  return kept.filter((item, i) => !item.heading || (kept[i + 1] !== undefined && !kept[i + 1].heading));
}

function pageGatesPass(item: NavigationItem, access: NavigationAccess): boolean {
  return passes(item.requiredPermissions, access) && (item.alsoRequiredPermissions ?? []).every((g) => passes(g, access));
}

/** One gate: the user needs one of the permissions. Same rules as the pre-P137 menu (guests only see External pages). */
function passes(required: AppPermissions[] | undefined, access: NavigationAccess): boolean {
  if (access.isGuest) return !!required?.includes(AppPermissions.EXTERNAL);
  if (!required?.length || access.has(AppPermissions.ADMIN)) return true;
  return required.some((p) => access.has(p));
}
